import { Injectable, numberAttribute } from '@angular/core';
import { ValidatorFn, ValidationErrors, FormGroup, AbstractControl } from '@angular/forms';

@Injectable({providedIn: 'root'})
export class ValidationService {

  static getValidatorErrorMessage(validatorName: string, validatorValue?: any) {
    const config = {
      'required': 'Required',
      'invalidCreditCard': 'Is invalid credit card number',
      'invalidEmailAddress': 'Invalid email address (name@example.com)',
      'invalidPassword': 'Password must be at least 8 characters long, contain at least one lower case letter, one upper case letter, one digit, one special character and cannot have spaces.',
      'passwordConfirm': 'Password and Confirm Password do not match.',
      'nameDuplicate': 'Name is already taken.',
      'emailValidatorDuplicate': 'Email address is already taken.',
      'minlength': `Minimum length is ${validatorValue ? validatorValue.requiredLength : ''}`,
      'maxlength': `Maximum length is ${validatorValue ? validatorValue.requiredLength : ''}`,
      'commaDelimitedNumbers': 'Value must be comma separated numbers without spaces',
      'commaSpacePipesNewLinesDelimitedNumbers': 'Value must be one or more numerical IDs sperated by comma, pipe, space or tab, or one per line',
      'LettersNumbersCommaUnderscore': 'Value must be letters, numbers, hyphen or underscore only and may not end with a space.',
      'nonNegativeDecimal100': 'Value must be a decimal between 0 and 100',
      'positiveDecimal100': 'Value must be a decimal greater than 0 and less than or equal to 100',
      'percent': 'Value must be a number between 0 and 100',
      'rmLowHigh': 'Risk Measure Lower Bound must be lower than Risk Measure Upper Bound',
      'lowHigh': 'Lower Bound must be lower than Upper Bound',
      'minMaxLoss': 'Min Loss must be lower than Max Loss',
      'positiveInt': 'Value must be a positive integer',
      'positiveDecimal': 'Value must be a positive decimal',
      'positiveDecimalGreaterThan1': 'Value must be an decimal greater than 1',
      'cantEndWithPeriod': 'Value cannot end with a decimal point',
      'maxInt': 'Value must be less than or equal to 2147483647',
      'RequiredCustom_NotJustSpaces' : 'Required',
      'Decimal': 'Value must be a valid decimal',
      'optGoodHigher': 'Good value must be greater than bad value for this metric',
      'optBadHigher': 'Bad value must be greater than good value for this metric',
      'seasonalityTotal100': 'Seasonality total must equal 100%',
      'dropDownNotEqualTo': 'Please choose a valid value',
      'unlimitedText': 'Value must be Unlimited or a valid positive decimal',
      'basisRiskMultiple': 'Duplicate Basis Risk',
      'liveCatEvents': 'Events for Live Cat report must be comma delimited list of numeric EventIDs',
      'analysisItemReservedWord': 'Analysis Item Name can not be "Subject", "Ceded" or "Loss"',
      'analysisItemNameUnique': 'Analysis Item Names must be unique',
      'analysisDeterministicNameUnique': 'Deterministic Trial Names must be unique',
      'lessThanNonillion': 'Numbers exceeding 1 Nonillion (1 with 30 zeros) may cause Excel exports to fail to load correctly.',
      'unlimitedOccRequiresAgg': 'Unlimted Occ Limit requires Aggregate Limit to be greater than 0.',
      'unlimitedReinstatementsRequiresAgg': 'Unlimted Reinstatements requires Aggregate Limit to be greater than 0.',
      'contractGreaterThanOneYear': 'Total contract date cannot exceed 1 calendar year',
      'inceptionDateLessThanExpirationDate': 'Inception Date must be before Expiration Date',
      'inceptionDateWhenExpirationDate': 'Inception Date must exist when updating the Expiration Date',
      'expirationDateWhenInceptionDate': 'Expiration Date must exist when updating the Inception Date',
      'positiveInt365': 'Value must be an integer between 1 and 365',
      'marginalIDs': 'Marginal requires either a Loss File ID or Portfolio ID',
      'marginalLossID': 'Marginal Loss File details requires a Loss File ID',
      'marginalPortID': 'Marginal Portfolio details requires a Portfolio ID',
      'uniqueFeatureSetName': 'Must be unique',
      'wholeNumber': 'Value must be a positive or negative whole number',
      'noSpecialCharacters': 'Field cannot contain special characters',
      'noSpecialCharactersWorkSheet': 'Field cannot contain characters /, \\, ?, :, *, [ or ]',
      'noFileNameWindowsInvalidCharacters': 'Files names cannot contain the same invalid characters as windows',
      'SubjectCededLoss': 'Field cannot contain only the words Subject, Ceded, or Loss',
      '15NumberDecimalLengthWithoutCommasOrDecimals': 'Maximum length is 15 numbers',
      'Decimal2Precision': 'Value must be a valid decimal with no more than 2 precision.',
      'Decimal4Precision': 'Value must be a valid decimal with no more than 4 precision.',
      'Decimal12Precision': 'Value must be a valid decimal less than 1000 with no more than 12 precision.',
      'Percentage12PrecisionWithZero': 'Value must be a valid percentage (0% - 100%) with no more than 12 precision.',
      'Percentage2PrecisionWithZero': 'Value must be a valid percentage (0% - 100%) with no more than 2 precision.',
      'Percentage2PrecisionWithZeroAddPercentageSign': 'Value must be a valid percentage (0% - 100%) with no more than 2 precision.',
      'CorrelationRequiresClient': 'Policy Correlation requires a client to be selected.',
      'CorrelationRequiresIndependent': 'Line of Business and Region Correlation requires a independent allocation to be selected.',
      'max': `Number must be equal to or less than ${validatorValue ? validatorValue.max : '' }.`,
      'min': `Number must be equal to or greater than ${validatorValue ? validatorValue.min : '' }.`,
      'companyNameExists': 'Name is already taken',
      'todayOrFutureDate': 'Date must be today or later.'
    };

    return config[validatorName];
  }

  static uniqueFeatureSetName(compareList: any[], currentName: string) {

    return function(control) {
      if (control.value === null || control.value === '') {
        return null;
      }  else {
        if (currentName !== null && currentName.toLowerCase() === control.value.toLowerCase()) {
          return null;
        }

        const find = compareList.find( x => x.name.toLowerCase() === control.value.toLowerCase());

        if (find === undefined) {
          return null;
        } else {
          return { 'uniqueFeatureSetName': true };
        }
      }
    };
  }

  static creditCardValidator(control) {
    if (control.value == null) { return null; }
    // Visa, MasterCard, American Express, Diners Club, Discover, JCB
    if (control.value.match(/^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/)) {
      return null;
    } else {
      return { 'invalidCreditCard': true };
    }
  }

  static emailValidator(control) {
    if (control.value == null) { return null; }
    // RFC 2822 compliant regex
    if (control.value.match(/[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?/)) {
      return null;
    } else {
      return { 'invalidEmailAddress': true };
    }
  }

  static Decimal4Precision(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    // RFC 2822 compliant regex
    if (control.value.toString().match(/^\s*-?[1-9]\d*(\.\d{1,4})?\s*$/)) {
      return null;
    } else {
      return { 'Decimal4Precision': true };
    }
  }

  static Decimal4PrecisionWithZero(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    // RFC 2822 compliant regex
    if (control.value.toString().match(/^\s*-?[0-9]\d*(\.\d{1,4})?\s*$/)) {
      return null;
    } else {
      return { 'Decimal4Precision': true };
    }
  }

  static Decimal2PrecisionWithZero(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    // RFC 2822 compliant regex
    if (control.value.toString().match(/^\s*-?[0-9]\d*(\.\d{1,2})?\s*$/)) {
      return null;
    } else {
      return { 'Decimal2Precision': true };
    }
  }

  static Decimal2PrecisionWithZeroAddComma(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    const val = '0' + control.value.toString().trim().toLowerCase().replace(/,/g, '');

    if (val.match(/^\s*-?[0-9]\d*(\.\d{1,2})?\s*$/)) {
      const num = Number(val);
      if (isNaN(num) || num < 0) {
        return { 'Decimal2Precision': true };
      } else {
        let value = Number(num);
        let returnVal = value.toString();
        let res = num.toString().split(".");
        if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
          returnVal = value.toFixed(2);
        }
        var parts = returnVal.toString().split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        if (control.value !== parts.join(".")) {
          setTimeout(() => {
            control.setValue(parts.join("."));
          }, 0);
        }

        return null;
      }
    } else {
      return { 'Decimal2Precision': true };
    }
  }  

  static Decimal12PrecisionWithZero(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    // RFC 2822 compliant regex
    if (control.value.toString().match(/^\s*-?[0-9]\d*(\.\d{1,12})?\s*$/)) {
      if (+control.value < 1000) {
        return null;
      } else {
        return { 'Decimal12Precision': true };
      }
    } else {
      return { 'Decimal12Precision': true };
    }
  }

  static Percentage12PrecisionWithZero(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    // RFC 2822 compliant regex
    if (control.value.toString().match(/^\s*-?[0-9]\d*(\.\d{1,12})?\s*$/)) {
      if (+control.value >= 0 && +control.value < 1000) {
        return null;
      } else {
        return { 'Percentage12PrecisionWithZero': true };
      }
    } else {
      return { 'Percentage12PrecisionWithZero': true };
    }
  }

  static Percentage2PrecisionWithZero(control) {
    if (control.value == null || control.value.length === 0) { return null; }
    // RFC 2822 compliant regex
    if (control.value.toString().match(/^\s*-?[0-9]\d*(\.\d{1,2})?\s*$/)) {
      if (+control.value >= 0 && +control.value < 1000) {
        return null;
      } else {
        return { 'Percentage2PrecisionWithZero': true };
      }
    } else {
      return { 'Percentage2PrecisionWithZero': true };
    }
  }

  static Percentage2PrecisionWithZeroAddPercentageSign(control) {
    let error: any;

    if (control.value == null || control.value.toString().length === 0) {
      return null;
    } else if (control.value && control.value.toString().length > 0 && control.value.toString().trim() === '%') {
      setTimeout(() => {
        control.setValue(null);
      }, 0);       
      return null;
    }

    const removepercval = control.value.toString().trim().toLowerCase().replace(/,/g, '').replace(/%/g, '');

    const val = removepercval.length == 0? '0' + removepercval : removepercval;

    // RFC 2822 compliant regex
    if (val.toString().match(/^\s*-?[0-9]\d*(\.\d{1,2})?\s*$/)) {
      if (+val >= 0 && +val < 1000) {
        error = null;
      } else {
        error = { 'Percentage2PrecisionWithZeroAddPercentageSign': true };
      }
    } else {
      error = { 'Percentage2PrecisionWithZeroAddPercentageSign': true };
    }

    if (control.value.toString().indexOf('%') === -1 || control.value.toString().indexOf('%') !== control.value.toString().length - 1) {
      setTimeout(() => {
        control.setValue(control.value.toString().trim().toLowerCase().replace(/%/g, '') + '%');
      }, 0);          
    }      

    return error;
  }  

  static Percentage12PrecisionWithZeroAddPercentageSign(control) {
    let error: any;

    if (control.value == null || control.value.toString().length === 0) {
      return null;
    } else if (control.value && control.value.toString().length > 0 && control.value.toString().trim() === '%') {
      setTimeout(() => {
        control.setValue(null);
      }, 0); 
      return null;
    }

    const removepercval = control.value.toString().trim().toLowerCase().replace(/%/g, '');

    const val = removepercval.length == 0? '0' + removepercval : removepercval;
    // RFC 2822 compliant regex
    if (val.match(/^\s*-?[0-9]\d*(\.\d{1,12})?\s*$/)) {
      if (+val >= 0 && +val < 1000) {
        error = null;
      } else {
        error = { 'Percentage12PrecisionWithZero': true };
      }
    } else {
      error = { 'Percentage12PrecisionWithZero': true };
    }

    if (control.value.toString().indexOf('%') === -1 || control.value.toString().indexOf('%') !== control.value.toString().length - 1) {
      setTimeout(() => {
        control.setValue(control.value.toString().trim().toLowerCase().replace(/%/g, '') + '%');
      }, 0);          
    }      

    return error;
  }

  static Percentage12PrecisionWithZeroAddPercentageSignNoLimit(control) {
    let error: any;

    if (control.value == null || control.value.toString().length === 0) {
      return null;
    } else if (control.value && control.value.toString().length > 0 && control.value.toString().trim() === '%') {
      setTimeout(() => {
        control.setValue(null);
      }, 0); 
      return null;
    }

    const val = '0' + control.value.toString().trim().toLowerCase().replace(/%/g, '');

    // RFC 2822 compliant regex
    if (val.match(/^\s*-?[0-9]\d*(\.\d{1,12})?\s*$/)) {
    } else {
      error = { 'Percentage12PrecisionWithZero': true };
    }

    if (control.value.toString().indexOf('%') === -1 || control.value.toString().indexOf('%') !== control.value.toString().length - 1) {
      setTimeout(() => {
        control.setValue(control.value.toString().trim().toLowerCase().replace(/%/g, '') + '%');
      }, 0);          
    }      

    return error;
  }

  // static Percentage2PrecisionWithZero(control) {
  //   if (control.value == null || control.value.length === 0) { return null; }
  //   // RFC 2822 compliant regex
  //   if (control.value.toString().match(/^\s*-?[0-9]\d*(\.\d{1,2})?\s*$/)) {
  //     if (+control.value >= 0 && +control.value <= 100) {
  //       return null;
  //     } else {
  //       return { 'Percentage2PrecisionWithZero': true };
  //     }
  //   } else {
  //     return { 'Percentage2PrecisionWithZero': true };
  //   }
  // }

  static passwordValidator(control) {
    if (control.value == null) { return null; }
    // {6,100}           - Assert password is between 6 and 100 characters
    // (?=.*[0-9])       - Assert a string has at least one number
    // from tigereye ^((?=^[\S])(?=.{8,})(?=.*\d)(?=.*[a-zA-Z])(?=.*[!\u0022#$%&'()*+,./:;<=>?@[\]\^_`{|}~-]).*)[\S]$
    // ^(?! )(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}[\S]$
    // min 8 chars, 1 number, 1 upper, 1 lower, 1 special  ^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&]{8,}
    // (?!.*\s)
    if (control.value.match(/^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#?!@$%^&*-])[A-Za-z\d#?!@$%^&*-](?!.*\s)/)) {
      return null;
    } else {
      return { 'invalidPassword': true };
    }
  }

  static noSpecialCharacters(control) {
    if (control.value == null) { return null; }
    if (control.value.match(/^[a-zA-Z0-9\s\-]*$/)) {
      return null;
    } else {
      return { 'noSpecialCharacters': true };
    }
  }

  static noFileNameWindowsInvalidCharacters(control) {
    if (control.value == null) { return null; }
    if (!control.value.match(/[<>:"/\|?*]/g)) {
      return null;
    } else {
      return { 'noFileNameWindowsInvalidCharacters': true };
    }
  }

  static validWorkSheetName(control) {
    if (control.value == null) { return null; }
    if (!/[*\[\]\\/:\?]/g.test(control.value)) {
      return null;
    } else {
      return { 'noSpecialCharactersWorkSheet': true };
    }
  }

  static passwordConfirmValidator(control) {
    const v = control.value;

    return { 'passwordConfirm': true };

  }
  static commaDelimitedNumbers(allowNull = false) {

    return function(control) {
      if ((control.value == null || control.value === '') && allowNull === true) {
        return { 'commaDelimitedNumbers': true };
      }

      if (control.value == null && allowNull === false) { return null; }

      if (control.value.match(/^([0-9])+(,[0-9]+)*$/)) {
        return null;
      } else {
        return { 'commaDelimitedNumbers': true };
      }
    };
  }

  static commaSpacePipesNewLinesDelimitedNumbers(allowNull = false) {

    return function(control) {
      if ((control.value == null || control.value === '') && allowNull === true) {
        return { 'commaSpacePipesNewLinesDelimitedNumbers': true };
      }

      if (control.value == null && allowNull === false) { return null; }
      
      if (control.value.match(/^[0-9,| \t\r\n]*$/)) {
        return null;
      } else {
        return { 'commaSpacePipesNewLinesDelimitedNumbers': true };
      }
    };
  }

  static LettersNumbersCommaUnderscore(control) {
    if (control.value == null) { return null; }

    try {
      if (control.value.match(/^[a-zA-Z0-9_ -]+( [a-zA-Z0-9_ -]+)*$/)) {
        return null;
      } else {
        return { 'LettersNumbersCommaUnderscore': true };
      }
    } catch (e) {
      return null;
    }
  }

  static SubjectCededLoss(control) {
    if (control.value == null) { return null; }

    try {
      if (control.value.toLowerCase().trim() === 'subject' || control.value.toLowerCase().trim() === 'ceded' || control.value.toLowerCase().trim() === 'loss') {
        return { 'SubjectCededLoss': true };
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  static RequiredCustom_NotJustSpaces(control) {
    if (control.value == null) { return null; }

    if (/\S/.test(control.value) || control.value.length === 0) {
      return null;
    } else {
      return { 'RequiredCustom_NotJustSpaces': true };
    }
  }

  static positiveInt(control) {
    if (control.value == null || control.value.length === 0) { return null; }

    if ((parseFloat(control.value) === parseInt(control.value, 10)) && !isNaN(control.value)) {
      if (parseInt(control.value, 10) >= 0) {
        return null;
      } else {
        return { 'positiveInt': true };
      }
    } else {
      return { 'positiveInt': true };
    }
  }

  static todayOrFutureDate(control) {
    if (control.value == null) { return null; }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateToCompare = new Date(control.value);
    dateToCompare.setHours(0, 0, 0, 0);

    if (dateToCompare < today) {
      return { 'todayOrFutureDate': true };
    }
  }

  static dropdownNotEqualTo = (exclusion: string) => {
    return (control) => {
      if (control.value == null || control.value.length === 0) { return null; }

      if (control.value.toString() === exclusion) {
        return { 'dropDownNotEqualTo': true};
      } else {
        return null;
      }
    };
  };

  static positiveDecimal(control) {
    if (control.value == null) { return null; }

      const val = '0' + control.value.toString().trim().toLowerCase().replace(/,/g, '');

      // check if the last number of the entry is a "." and if it is, error it out
      if (val.length > 1 && val.charAt(val.length - 1) === '.') {
        return { 'cantEndWithPeriod': true };
      }

      if (val.match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
        const num = Number(val);
        if (isNaN(num) || num < 0) {
          return { 'positiveDecimal': true };
        } else {
          let value = Number(num);
          let returnVal = value.toString();
          let res = num.toString().split(".");
          if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
            returnVal = value.toFixed(2);
          }
          var parts = returnVal.toString().split(".");
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          control.value = parts.join(".");

          return null;
        }
      } else {
        return { 'positiveDecimal': true };
      }
  }

  static positiveDecimalEmptyAsNull(control) {
    if (control.value == null) { return null; }

      if (control.value.length === 0)
      {
        control.value = null;
        return null;
      }

      const val = '0' + control.value.toString().trim().toLowerCase().replace(/,/g, '');

      // check if the last number of the entry is a "." and if it is, error it out
      if (val.length > 1 && val.charAt(val.length - 1) === '.') {
        return { 'cantEndWithPeriod': true };
      }

      if (val.match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
        const num = Number(val);
        if (isNaN(num) || num < 0) {
          return { 'positiveDecimal': true };
        } else {
          let value = Number(num);
          let returnVal = value.toString();
          let res = num.toString().split(".");
          if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
            returnVal = value.toFixed(2);
          }
          var parts = returnVal.toString().split(".");
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          control.value = parts.join(".");

          return null;
        }
      } else {
        return { 'positiveDecimal': true };
      }
  }

  static positiveDecimalStripCommas(control) {
    if (control.value == null) { return null; }

      const val = '0' + control.value.toString().trim().toLowerCase().replace(/,/g, '');

      // check if the last number of the entry is a "." and if it is, error it out
      if (val.length > 1 && val.charAt(val.length - 1) === '.') {
        return { 'cantEndWithPeriod': true };
      }

      if (val.match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
        const num = Number(val);
        if (isNaN(num) || num < 0) {
          return { 'positiveDecimal': true };
        } else {
          let value = Number(num);
          let returnVal = value.toString();
          let res = num.toString().split(".");
          if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
            returnVal = value.toFixed(2);
          }
          var parts = returnVal.toString().split(".");
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          control.value = parts.join(".").replace(/,/g, '');

          return null;
        }
      } else {
        return { 'positiveDecimal': true };
      }
  }

  static positiveDecimalGreaterThan1(control) {
    if (control.value == null) { return null; }

    const val = '0' + control.value.toString().trim().toLowerCase().replace(/,/g, '');

    // check if the last number of the entry is a "." and if it is, error it out
    if (val.length > 1 && val.charAt(val.length - 1) === '.') {
      return { 'cantEndWithPeriod': true };
    }

    if (val.match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
      const num = Number(val);
      if (isNaN(num) || num < 1) {
        return { 'positiveDecimalGreaterThan1': true };
      } else {
        const value = Number(num);
        let returnVal = value.toString();
        const res = num.toString().split('.');
        if (res.length === 1 && (res[1] !== undefined && res[1].length < 2)) {
          returnVal = value.toFixed(2);
        }
        const parts = returnVal.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        control.value = parts.join('.');
        return null;
      }
    } else {
      return { 'positiveDecimalGreaterThan1': true };
    }
  }

  static positiveDecimalWithKMBT(control) {
    if (control.value == null) { return null; }
      control.value = '0' + control.value.toString().trim();
      let val = control.value.toString().trim().toLowerCase().replace(/,/g, '');
      const lastChar = val.substr(val.length - 1);
      const charAry = ['', 'k', 'm', 'b', 't'];
      const charIndex = charAry.indexOf(lastChar);

      if (charIndex > 0) {
        val = val.substr(0, val.length - 1);
      }
      val = '0' + val;

      // check if the last number of the entry is a "." and if it is, error it out
      if (val.length > 1 && val.charAt(val.length - 1) === '.') {
        return { 'cantEndWithPeriod': true };
      }

      if (val.match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
        const num = Number('0' + val);
        if (isNaN(num) || num < 0) {
          return { 'positiveDecimal': true };
        } else {
          let value = Number(num);
          let returnVal = value.toString();
          let res = num.toString().split(".");

          if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
            returnVal = value.toFixed(2);
          }
          if (charIndex > 0) {
            returnVal = returnVal + charAry[charIndex];
          }
          var parts = returnVal.toString().split(".");
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          control.value = parts.join(".");
          return null;
        }
      } else {
        return { 'positiveDecimal': true };
      }
  }

  static maxNumericalLength(maxLength: number) {
    return (control) => {
      if (maxLength > 0) {
        if (control.value == null) { return null; }
        const val = control.value.toString().trim().toLowerCase().replace(/,/g, '');
        const fixedVal = val.replace(/\./g, '');
        if (fixedVal.length > maxLength) {
          return { '15NumberDecimalLengthWithoutCommasOrDecimals': true };
        } else {
          return null;
        }
      }
    };
  }

  static liveCatEventIDs(control) {
    if (control.value == null) { return null; }
    const eventIDs = control.value.split(',');
    let eventsInvalid = false;
    eventIDs.forEach((id)  => {
      if (isNaN(id.trim())) {
        eventsInvalid = true;
      }
      const num = Number(id.trim());
      if (Number.isNaN(num)|| num < 0) {
        eventsInvalid = true;
      }
    });
    if (!eventsInvalid) {
      return null;
    } else {
      return { 'liveCatEvents': true };
    }

  }

  static unlimitedText(control) {
    if (control.value == null || control.value === 'Unlimited') { return null; }

    let val = '0' + control.value.toString().trim().toLowerCase().replace(/,/g, '');
    const lastChar = val.substr(val.length - 1);

    // check if the last number of the entry is a "." and if it is, error it out
    if (val.length > 1 && lastChar === '.') {
      return { 'cantEndWithPeriod': true };
    }

    const charAry = ['', 'k', 'm', 'b', 't'];
    const charIndex = charAry.indexOf(lastChar);

    if (charIndex > 0) {
      val = val.substr(0, val.length - 1);
    }


    if (val.match(/^[0-9e]+(\.[0-9e]*){0,1}$/g)) {
      const num = Number(val);
      if (isNaN(num) || num < 0) {
        return {'unlimitedText': true};
      } else {
        let value = Number(num);
        let returnVal = value.toString();
        let res = num.toString().split(".");
        if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
          returnVal = value.toFixed(2);
        }
        if (charIndex > 0) {
          returnVal = returnVal + charAry[charIndex];
        }
        //handle commas and decimals
        var parts = returnVal.toString().split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        control.value = parts.join(".");
        return null;
      }
    } else if (val !== '-1') {
      return {'unlimitedText': true};
    }
  }

  static maxInt(control) {
    if (control.value == null || control.value.length === 0) { return null; }

    if (parseInt(control.value, 10) <= 2147483647) {
      return null;
    } else {
      return { 'maxInt': true };
    }
  }

  static wholeNumber(allowNull: boolean = false) {
    return (control) => {
      if (allowNull && (control.value == null || control.value === '')) {
        return { 'wholeNumber': true };
      }

      if (!allowNull && control.value == null) { return null; }

      if (control.value.toString().match(/^-?(\d+|\d{1,3}(,\d{3})*)(\.\d+)?$/)) {
        return null;
      }

      return { 'wholeNumber': true };

    };
  }

   static nonNegativeDecimal100(control) {
    if (control.value == null) { return null; }
     control.value = '0' + control.value.toString().trim();
    if (control.value.toString().match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
      const num = Number('0' + control.value);
      if (num > 100) {
        return { 'nonNegativeDecimal100': true };
      }else {
        let value = Number(num);
        let returnVal = value.toString();
        let res = num.toString().split(".");
        if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
          returnVal = value.toFixed(2);
        }
        control.value = returnVal;
        return null;
      }


    } else {
      return { 'nonNegativeDecimal100': true };
    }
  }

  static positiveDecimal100(control) {
    if (control.value == null) { return null; }
    control.value = '0' + control.value.toString().trim();
    if (control.value.toString().match(/^[0-9]+(\.[0-9]*){0,1}$/g)) {
      const num = Number('0' + control.value);
      if (num <= 0 || num > 100) {
        return { 'positiveDecimal100': true };
      }
      else {
        let value = Number(num);
        let returnVal = value.toString();
        let res = num.toString().split(".");
        if(res.length == 1 && (res[1] !== undefined && res[1].length < 2)) {
          returnVal = value.toFixed(2);
        }
        control.value = returnVal;
        return null;
      }

    } else {
      return { 'positiveDecimal100': true };
    }
  }

  static percent() {

    return function(control) {

      if (control.value == null || control.value === '') {
        return null;
      }
      const num = Number(control.value);
      if (isNaN(num)) {

        return {'percent': true};
      } else {
        if (num < 0 || num > 100) {

          return {'percent': true};
        }
        return null;
      }
    }


  }

  static lessThanNonillion(control) {
    if (control.value == null) { return null; }

    const val = control.value.toString().replace(/,/g, '');
    if (val.match(/^[0-9eE]+(\.[0-9]*){0,1}$/g)) {
      const num = Number(val);
      if ( num > 1E30) {
        return { 'lessThanNonillion': true };
      }
      return null;
    } else {
      return null;
    }
  }

  static positiveInt100(control) {
    if (control.value == null) { return null; }

    if (control.value.toString().match(/^(?:100|[1-9]?[0-9])$/g) ) {
      const num = Number(control.value);
      if (num <= 0 || num > 100) {
        return { 'positiveInt100': true };
      }
      return null;
    } else {
      return { 'positiveInt100': true };
    }
  }

  static positiveInt365(control) {
    if (control.value == null) { return null; }

    if (control.value.toString().match(/^(?:36[0-5]|3[0-5][0-9]|[12][0-9][0-9]|[1-9][0-9]|[1-9])$/g) ) {
      const num = Number(control.value);
      if (num <= 0 || num > 365) {
        return { 'positiveInt365': true };
      }
      return null;
    } else {
      return { 'positiveInt365': true };
    }
  }

  static decimal(control) {
    if (control.value == null) { return null; }
    const num = Number(control.value.toString().replace(/,/g , ''));
    if (Number.isNaN(num)) {
      return { 'Decimal': true };
    } else {
      return null
    }
  }

  static rmLowHigh(group) {
    const controlLow = group.get('lower');
    const controlUpper = group.get('upper');
    if (controlLow.value == null || controlUpper.value == null) { return null; }

    const numLow = Number(controlLow.value);
    const numHigh = Number(controlUpper.value);
    if (numLow < numHigh) {
      return null;
    }
    return { 'rmLowHigh': true };
  }

  static lowHigh(group) {
    const controlLow = group.get('lower');
    const controlUpper = group.get('upper');
    if (controlLow.value == null || controlUpper.value == null) { return null; }

    const numLow = Number(controlLow.value);
    const numHigh = Number(controlUpper.value);
    if (numLow < numHigh) {
      return null;
    }
    return { 'lowHigh': true };
  }

  static minMaxLoss(group) {
    const controlLow = group.get('sevDistMinLoss');
    const controlUpper = group.get('sevDistMaxLoss');
    if (controlLow.value == null || controlUpper.value == null) { return null; }

    const numLow = Number(controlLow.value.toString().replace(/,/g, ''));
    const numHigh = Number(controlUpper.value.toString().replace(/,/g, ''));
    if (numLow < numHigh) {
      return null;
    }
    return { 'minMaxLoss': true };
  }

  static lowHighUnlimited(group) {
    const controlLow = group.get('lower');
    const controlUpper = group.get('upper');
    if (controlLow.value == null || controlUpper.value == null || controlUpper.value === 'Unlimited' || controlUpper.value === '-1') { return null; }

    const numLow = Number(controlLow.value.toString().replace(/,/g, ''));
    const numHigh = Number(controlUpper.value.toString().replace(/,/g, ''));
    if (numLow < numHigh) {
      return null;
    }
    return { 'lowHigh': true };
  }

  static optGoodHigher(group) {
    const controlGood = group.get('goodValue');
    const controlBad = group.get('badValue');

    if (controlGood.value === '' || controlBad.value === '' || controlGood.value === null || controlBad.value === null) { return null; }

    const numGood = Number(controlGood.value);
    const numBad = Number(controlBad.value);
    if (Number.isNaN(numGood) || Number.isNaN(numBad)) { return null; }
    if (numGood > numBad) {
      return null;
    }
    return { 'optGoodHigher': true };
  }

  static optBadHigher(group) {
    const controlGood = group.get('goodValue');
    const controlBad = group.get('badValue');

    if (controlGood.value === '' || controlBad.value === '' || controlGood.value === null || controlBad.value === null) { return null; }

    const numGood = Number(controlGood.value);
    const numBad = Number(controlBad.value);
    if (Number.isNaN(numGood) || Number.isNaN(numBad)) { return null; }
    if (numGood < numBad) {
      return null;
    }
    return { 'optBadHigher': true };
  }

  static seasonalityTotal100(group) {
    let total = 0;
    Object.getOwnPropertyNames(group.controls).forEach((name: string) => {
      if (name.indexOf('seasonDist') === 0) {
        const num = Number(group.get(name).value);
        if (!isNaN(num)) {
          total += num;
        }
      }
    });

    if (total >= 99.999 && total <= 100.001) {
      return null;
    }
    return { 'seasonalityTotal100': true };
  }

  static updateSeasonalityTotal100(group) {
    let total = 0;
    Object.getOwnPropertyNames(group.controls).forEach((name: string) => {
      if (name.includes('Pct')) {
        const num = Number(group.get(name).value);
        if (!isNaN(num)) {
          total += num;
        }
      }
    });

    if (total >= 99.999 && total <= 100.001) {
      return null;
    }
    return { 'updateSeasonalityTotal100': true };
  }

  static analysisItemReservedWord(control) {
    if (control.value == null) { return null; }

    const val = control.value.toLowerCase();
    if (val === 'subject' || val === 'ceded' || val === 'loss') {
      return { 'analysisItemReservedWord': true };
    } else {
      return null;
    }
  }

  static analysisItemNameUnique(model: any, layer: any): ValidatorFn {
    return (control) => {
      const myName = control.value;
      if (model.layers.filter((item: any) => { return item.values.analysisItemID !== layer.values.analysisItemID; })
          .filter((item2: any) => { return item2.values.analysisItemName.toLowerCase() === myName.toLowerCase(); }).length > 0) {
        return {'analysisItemNameUnique': true}
      } else {
        return null;
      }
    }
  }

  static analysisDeterministicNameUnique(model: any, trial: any): ValidatorFn {
    return (control) => {
      const myName = control.value;
      if (model.trials.filter((t: any) => { return t.trialID !== trial.trialID; })
          .filter((t2: any) => { return t2.name === myName; }).length > 0) {
        return {'analysisDeterministicNameUnique': true}
      } else {
        return null;
      }
    }
  }

  static unlimitedOccRequiresAgg(group) {
    const controlOcc = group.get('limit');
    const controlAgg = group.get('aggregateLimit');
    if (controlOcc.value == null || controlAgg.value == null) { return null; }

    const numHigh = Number(controlAgg.value.toString().replace(/,/g, ''));
    if (controlOcc.value === 'Unlimited' && numHigh <= 0) {
      return { 'unlimitedOccRequiresAgg': true };
    }
    return null;
  }

  static unlimitedReinstatementsRequiresAgg(group) {
    const controlOcc = group.get('riString');
    const controlAgg = group.get('aggregateLimit');
    if (controlOcc.value == null || controlAgg.value == null) { return null; }

    const numHigh = Number(controlAgg.value.toString().replace(/,/g, ''));
    if (controlOcc.value === '-1@0' && numHigh <= 0) {
      return { 'unlimitedReinstatementsRequiresAgg': true };
    }
    return null;
  }

  static contractGreaterThanOneYear(group) {
    const controlInception = group.get('inceptionDate');
    const controlExpiration = group.get('expirationDate');
    if (controlInception.value == null || controlExpiration.value == null) { 
      return null; 
    }

    const id = new Date(controlInception.value);
    const maxDt = new Date(id.getFullYear() + 1, id.getMonth(), id.getDate() - 1);
    const ed = new Date(controlExpiration.value);
    if (ed > maxDt) {
      return { 'contractGreaterThanOneYear': true };
    }
    return null;
  }

  static inceptionBeforeExpiration(group) {
    const controlInception = group.get('inceptionDate');
    const controlExpiration = group.get('expirationDate');

    const id = new Date(controlInception.value);
    const ed = new Date(controlExpiration.value);

    //if both good dates check them.  Forms should have other validation to account for dates being good
    if (!isNaN(id.getTime()) && !isNaN(ed.getTime()) && id.getTime() > 0 && ed.getTime() > 0) {
      if (ed <= id) {
        return { 'inceptionDateLessThanExpirationDate': true };
     }
    }
    return null;
  }

  static inceptionWhenExpiration(group) {
    const controlInception = group.get('inceptionDate');
    const controlExpiration = group.get('expirationDate');

    const id = new Date(controlInception.value);
    const ed = new Date(controlExpiration.value);

    //if one or the other is null or undefined, that's not ok, return the error
    if ((id.getTime() === 0 || isNaN(id.getTime())) && (!isNaN(ed.getTime()) && ed.getTime() !== 0)) {
      return { 'inceptionDateWhenExpirationDate': true };
    }

    if ((ed.getTime() === 0 || isNaN(ed.getTime())) && (!isNaN(id.getTime()) && id.getTime() !== 0)) {
      return { 'expirationDateWhenInceptionDate': true };
    }
    //if both are good dates or both empty dates then we are good
    return null;
  }

  static marginalLossPortfolioIDReq(group) {
    const lossFileID = group.get('lossFileID');
    const portfolioID = group.get('portfolioID');
    const lossPeril = group.get('lossPeril');
    const lossRegion = group.get('lossRegion');
    const lossLOB = group.get('lossLOB');
    const useAdjusted = group.get('useAdjusted');
    const includeCeded = group.get('includeCeded');
    const portPeril = group.get('portPeril');
    const portRegion = group.get('portRegion');
    const portLOB = group.get('portLOB');
    const ccrmLower = group.get('ccrmLower');
    const ccrmUpper = group.get('ccrmUpper');
    if (lossFileID.value == null && portfolioID.value == null) {
      if (
        lossPeril.value == null &&
        lossRegion.value == null &&
        lossLOB.value == null &&
        useAdjusted.value == null &&
        includeCeded.value == null &&
        portPeril.value == null &&
        portRegion.value == null &&
        portLOB.value == null &&
        ccrmLower.value == null &&
        ccrmUpper.value == null
      ) {
        return null;
      } else {
        return {'marginalIDs': true};
      }
    }
    /*else if (lossFileID.value == null) {
      if(
        lossPeril.value == null &&
        lossRegion.value == null &&
        lossLOB.value == null
      ) {
        return null;
      }else {
        return {'marginalLossID': true};
      }
    }else if (portfolioID.value == null) {
      if (
        portPeril.value == null &&
        portRegion.value == null &&
        portLOB.value == null
      ) {
        return null;
      } else {
        return {'marginalPortID': true};
      }
    }*/
    return null;


  }

  static CorrelationRequiresClient(eltForm): ValidatorFn {
 
    return (control: AbstractControl): ValidationErrors | null => {
   
      let correlation: boolean = control.value;

      if (correlation && +eltForm.controls.ClientID.value === -1) {
        return { 'CorrelationRequiresClient': true };
      }
      return null;
    }

  }

  static CorrelationRequiresIndependent(control) {
 
    const correlation = control.value;

    if ((correlation !== null && correlation) && +control.parent.controls.AllocateLosses.value !== 0) {
      return { 'CorrelationRequiresIndependent': true };
    }
    return null;
  }

  constructor() { }

}
